import { z } from 'zod';

/**
 * Global cross-entity search — the wire contract for `GET /api/search?q=…`,
 * the endpoint behind the ⌘K command palette.
 *
 * The palette used to search only the NAV DESTINATIONS the current role can
 * reach, so finding a person or a site meant already knowing which screen
 * lists them. This adds DATA hits alongside the pages, which makes the endpoint
 * a cross-entity read surface — and therefore the exact shape of thing that
 * leaks if it is written with its own scoping. It has none: the route composes
 * `resolveReportScope` (utils/reportScope.ts), the same authority fragment
 * every report family already uses, and mirrors the ROLE + FEATURE matrix the
 * nav uses for the destination page of each entity. See search.service.ts.
 *
 * The caps below are part of the security contract, not a UX preference:
 * without them a caller could page the endpoint into a full workforce export.
 */

// The three numeric bounds live in the zod-free `../searchLimits.js` so the
// command palette — mounted by AppShell, so on every page's boot path — can
// read them without loading this module. Re-exported here so existing imports
// keep working.
export {
  SEARCH_MIN_QUERY_LENGTH,
  SEARCH_MAX_QUERY_LENGTH,
  SEARCH_PER_ENTITY_LIMIT,
} from '../searchLimits.js';
import {
  SEARCH_MIN_QUERY_LENGTH,
  SEARCH_MAX_QUERY_LENGTH,
} from '../searchLimits.js';

/**
 * Hard overall cap, applied AFTER the per-entity cap. Five entities × five hits
 * is 25, so this genuinely trims — and it means no combination of flags and
 * roles can ever widen one response beyond twenty rows.
 */
export const SEARCH_TOTAL_LIMIT = 20;

export const SearchEntityTypeSchema = z.enum([
  'employee',
  'worksite',
  'department',
  'designation',
  'supplier',
]);
export type SearchEntityType = z.infer<typeof SearchEntityTypeSchema>;

export const SearchQuerySchema = z.object({
  q: z
    .string()
    .trim()
    .min(SEARCH_MIN_QUERY_LENGTH)
    .max(SEARCH_MAX_QUERY_LENGTH),
});
export type SearchQuery = z.infer<typeof SearchQuerySchema>;

/**
 * One result row. Uniform across every entity so the palette renders a mixed
 * list without a per-type branch:
 *   - `label`    the primary line (a person's name, a site's name)
 *   - `sublabel` the secondary line (employee code, site code + type) — `null`
 *                when the record genuinely has none, never an empty string
 *   - `to`       the CLIENT-SIDE route that opens the record. Resolved on the
 *                server so the palette never has to know each entity's URL shape.
 */
export const SearchHitSchema = z.object({
  type: SearchEntityTypeSchema,
  id: z.string(),
  label: z.string(),
  sublabel: z.string().nullable(),
  to: z.string(),
});
export type SearchHit = z.infer<typeof SearchHitSchema>;

export const SearchGroupSchema = z.object({
  type: SearchEntityTypeSchema,
  hits: z.array(SearchHitSchema),
});
export type SearchGroup = z.infer<typeof SearchGroupSchema>;

export const SearchResultSchema = z.object({
  /** Echoed back so a client can discard a response that lost the race. */
  query: z.string(),
  /** Only groups with at least one hit are present; order is stable. */
  groups: z.array(SearchGroupSchema),
  /** Total hits across every group, after both caps. */
  total: z.number(),
  /** True when a cap trimmed the result — the palette says "showing the first…". */
  truncated: z.boolean(),
});
export type SearchResult = z.infer<typeof SearchResultSchema>;
